import Serializer, { type DeserializeArguments } from './Serializer';
import { type Element as SlateElement } from 'slate';
import {
  isElementNode,
  isHTMLElementNode,
  isTextNode,
} from '../helpers/serializerHelpers';

function isTopLevelNode(el) {
  if (!el.parentNode) return true;
  if (!el.parentNode.tagName) return true;

  return (
    el.parentNode.tagName.toLowerCase() === 'body' ||
    el.parentNode.id.startsWith('docs-internal-guid')
  );
}

class SerializerCleanup extends Serializer {
  /**
   * Gmail represents empty lines in plain text emails as
   * `<div><br></div>`. We should convert them to `<p />`.
   */
  static emptyDivBrToParagraphSerializer(el: Node): SlateElement | undefined {
    if (
      !isHTMLElementNode(el) ||
      !['div', 'p'].includes(el.tagName.toLowerCase())
    ) {
      return;
    }

    if (el.childNodes.length !== 1) return;
    if (el.children.length !== 1) return;
    if (el.firstElementChild?.tagName?.toLowerCase() !== 'br') return;

    return {
      type: 'paragraph',
      children: [],
    };
  }

  /**
   * Unwrapped <br /> tags in the top-level of the document will get
   * wrapped in <p> tags, resulting in <p><br /></p>, or one too many
   * newlines. If we detect that we're a <br /> in the top level of
   * the document, we'll wrap ourselves -- without the <br />.
   */
  static cleanupTopLevelBrSerializer(el: Node): SlateElement | undefined {
    if (!isHTMLElementNode(el) || el.tagName.toLowerCase() !== 'br') return;
    if (!isTopLevelNode(el)) return;

    return {
      type: 'paragraph',
      children: [],
    };
  }

  /**
   * We get `\n`s followed by `br`s when we receive mail from Mandrill
   * and convert it to HTML. HTML ignores any whitespace surrounding a
   * <br /> tag, so we should remove it to more closely match how it
   * would normally be rendered.
   */
  static cleanupNewlinesAroundBrsSerializer(
    el: Node
  ): SlateElement | undefined {
    if (!isHTMLElementNode(el) || el.tagName.toLowerCase() !== 'p') return;

    Array.from(el.childNodes).forEach((node) => {
      if (isElementNode(node) && node.tagName.toLowerCase() === 'br') {
        const previousNode = node.previousSibling;
        if (previousNode && isTextNode(previousNode)) {
          previousNode.textContent = previousNode.textContent!.replace(
            /\s+$/,
            ''
          );
        }

        const nextNode = node.nextSibling;
        if (nextNode && isTextNode(nextNode)) {
          nextNode.textContent = nextNode.textContent!.replace(/^\s+/, '');
        }
      }
    });
  }

  static cleanupDoubleNewlinesSerializer(
    el: Node
  ): SlateElement | undefined | null {
    if (!isTextNode(el)) return;

    const previousNode = el.previousSibling;
    if (
      !previousNode ||
      (isElementNode(previousNode) &&
        previousNode.tagName.toLowerCase() === 'p')
    ) {
      if (el.textContent === '\n\n') {
        return null;
      }
    }
  }

  /**
   * Many TinyMCE tables / lists have an ending BR followed by a
   * newline or another block node. This ends up creating extra empty
   * lines, because they're converted into `\n`s by Slate, which show
   * up in Slate's `pre-wrap` editor.
   *
   * As best I can tell, the default behavior for brs in browsers is:
   * * If you're followed by an element with height, create a new line
   * * Otherwise, collapse into the next element
   *
   * Since we don't have that information at this point, I'm going to
   * do this instead:
   *
   * * If the br is the last element in an li, td, or p (or is only
   *   followed by a newline), strip it out.
   * * If the br is the last element before a ul / ol, strip it out.
   *
   * That fixes all of the spacing problems I've seen so far, and
   * should be about as light of a touch as we can have.
   */
  static cleanupBrNewlineSerializer(el: Node): SlateElement | undefined {
    if (!isHTMLElementNode(el)) return;

    if (['td', 'li', 'p'].includes(el.tagName.toLowerCase())) {
      const possibleBr = el.lastElementChild;

      if (possibleBr && possibleBr.tagName.toLowerCase() === 'br') {
        if (
          el.lastChild &&
          (el.lastChild === possibleBr ||
            (el.lastChild === possibleBr.nextSibling &&
              el.lastChild.textContent === '\n'))
        ) {
          if (el.lastChild !== possibleBr) {
            el.removeChild(el.lastChild);
          }
          el.removeChild(possibleBr);
        }
      }
    }

    for (const child of el.children) {
      if (
        child.tagName.toLowerCase() === 'br' &&
        child.nextSibling &&
        child.nextSibling.nodeType === el.ELEMENT_NODE &&
        ['ul', 'ol'].includes(
          (child.nextSibling as Element).tagName.toLowerCase()
        )
      ) {
        el.removeChild(child);
      }
    }
  }

  static removeStyleTagSerializer(el: Node): void {
    if (isHTMLElementNode(el) && el.tagName.toLowerCase() === 'style') {
      el.replaceChildren();
    }
  }

  /**
   * I am not sure how this was handled with version 0.47 but we are seeing
   * newlines between block elements being converted into unnecessary text
   * nodes. Strip them out.
   */
  static removeBlockLevelNewlines(el: Node): SlateElement | undefined {
    if (el.childNodes.length < 2) return;

    for (const child of el.childNodes) {
      if (
        child.nodeType === child.TEXT_NODE &&
        (!child.previousSibling ||
          (child.previousSibling &&
            child.previousSibling.nodeType === el.ELEMENT_NODE)) &&
        child.textContent === '\n'
      ) {
        el.removeChild(child);
      }
    }
  }

  /**
   * If there are two <br>s at the end of a block element remove one of them
   * since Slate will add a second newline after a trailing newline in a block.
   */
  static cleanupTrailingBrs(el: Node): void {
    if (el.childNodes.length === 0) return;

    const lastChild = el.lastChild;
    if (!lastChild || lastChild.nodeType !== el.ELEMENT_NODE) return;
    if ((lastChild as Element).tagName.toLowerCase() !== 'br') return;

    // Check if this is the last child of the parent, and its previous sibling
    // is also a <br>.
    if (
      lastChild.previousSibling &&
      lastChild.previousSibling.nodeType === el.ELEMENT_NODE &&
      (lastChild.previousSibling as Element).tagName.toLowerCase() === 'br'
    ) {
      el.removeChild(lastChild);
    }
  }

  static deserialize({ el }: DeserializeArguments): SlateElement | undefined {
    let result: SlateElement | undefined;

    // Generic cleanups.
    this.removeBlockLevelNewlines(el);
    this.removeStyleTagSerializer(el);
    this.cleanupBrNewlineSerializer(el);
    // TODO: seems unnecessary this.cleanupDoubleNewlinesSerializer(el);
    this.cleanupNewlinesAroundBrsSerializer(el);
    this.cleanupTrailingBrs(el);

    // Result generating cleanups.
    result ||= this.emptyDivBrToParagraphSerializer(el);
    result ||= this.cleanupTopLevelBrSerializer(el);

    return result;
  }

  static serialize(): React.ReactNode {
    return null;
  }
}

export default SerializerCleanup;
